'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import InnerPageHeader from '@/components/InnerPageHeader';
import Toast from '@/components/Toast';

export default function TrainerSignup() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  
  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    dateOfBirth: '',
    
    // Step 2: Professional Info
    bio: '',
    profilePhoto: '',
    certifications: [] as string[],
    yearsOfExperience: '',
    
    // Step 3: Specializations & Services
    specializations: [] as string[],
    servicesOffered: [] as string[],
    sessionPricing: '',
    
    // Step 4: Availability
    availability: {
      Sunday: { isAvailable: false, timeSlots: [] as string[] },
      Monday: { isAvailable: true, timeSlots: ['Morning', 'Evening'] },
      Tuesday: { isAvailable: true, timeSlots: ['Morning', 'Evening'] },
      Wednesday: { isAvailable: true, timeSlots: ['Morning', 'Evening'] },
      Thursday: { isAvailable: true, timeSlots: ['Morning', 'Evening'] },
      Friday: { isAvailable: true, timeSlots: ['Morning', 'Evening'] },
      Saturday: { isAvailable: false, timeSlots: [] as string[] },
    },
    
    // Step 5: Preferences
    preferredEnvironment: [] as string[],
    travelDistance: '',
    additionalNotes: '',
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfilePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setToast({ message: 'Please upload only PNG, JPG, JPEG, or WEBP image', type: 'error' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({ message: 'Image must be less than 5MB', type: 'error' });
      return;
    }

    // Compress and convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const MAX_SIZE = 800;
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > MAX_SIZE) {
            height *= MAX_SIZE / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width *= MAX_SIZE / height;
            height = MAX_SIZE;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx?.drawImage(img, 0, 0, width, height);
        
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
        setFormData(prev => ({
          ...prev,
          profilePhoto: compressedBase64
        }));
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleCertificationUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles = Array.from(files);
    
    newFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const MAX_WIDTH = 1200;
          const MAX_HEIGHT = 1200;
          
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          
          setFormData(prev => ({
            ...prev,
            certifications: [...prev.certifications, compressedBase64]
          }));
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleRemoveCertification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const handleSpecializationToggle = (specialization: string) => {
    setFormData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(specialization)
        ? prev.specializations.filter(s => s !== specialization)
        : [...prev.specializations, specialization]
    }));
  };

  const handleServiceToggle = (service: string) => {
    setFormData(prev => ({
      ...prev,
      servicesOffered: prev.servicesOffered.includes(service)
        ? prev.servicesOffered.filter(s => s !== service)
        : [...prev.servicesOffered, service]
    }));
  };

  const handleDayToggle = (day: string) => {
    setFormData(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day as keyof typeof prev.availability],
          isAvailable: !prev.availability[day as keyof typeof prev.availability].isAvailable
        }
      }
    }));
  };

  const handleTimeSlotToggle = (day: string, slot: string) => {
    setFormData(prev => {
      const dayAvailability = prev.availability[day as keyof typeof prev.availability];
      const hasSlot = dayAvailability.timeSlots.includes(slot);
      
      return {
        ...prev,
        availability: {
          ...prev.availability,
          [day]: {
            ...dayAvailability,
            timeSlots: hasSlot
              ? dayAvailability.timeSlots.filter(s => s !== slot)
              : [...dayAvailability.timeSlots, slot]
          }
        }
      };
    });
  };

  const handleEnvironmentToggle = (env: string) => {
    setFormData(prev => ({
      ...prev,
      preferredEnvironment: prev.preferredEnvironment.includes(env)
        ? prev.preferredEnvironment.filter(e => e !== env)
        : [...prev.preferredEnvironment, env]
    }));
  };

  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation for Step 1
    if (currentStep === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.location || !formData.dateOfBirth) {
        setToast({ message: 'Please fill in all required fields', type: 'error' });
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setToast({ message: 'Please enter a valid email address', type: 'error' });
        return;
      }

      // Age validation (must be 18+)
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      if (age < 18) {
        setToast({ message: 'You must be at least 18 years old to register as a trainer', type: 'error' });
        return;
      }
    }

    // Validation for Step 2
    if (currentStep === 2) {
      if (!formData.bio || formData.bio.trim().length < 100) {
        setToast({ message: 'Please write at least 100 characters in your bio', type: 'error' });
        return;
      }
      if (!formData.profilePhoto) {
        setToast({ message: 'Please upload your profile photo', type: 'error' });
        return;
      }
      if (formData.certifications.length === 0) {
        setToast({ message: 'Please upload at least one certification', type: 'error' });
        return;
      }
      if (!formData.yearsOfExperience) {
        setToast({ message: 'Please enter your years of experience', type: 'error' });
        return;
      }
    }

    // Validation for Step 3
    if (currentStep === 3) {
      if (formData.specializations.length === 0) {
        setToast({ message: 'Please select at least one specialization', type: 'error' });
        return;
      }
      if (formData.servicesOffered.length === 0) {
        setToast({ message: 'Please select at least one service you offer', type: 'error' });
        return;
      }
    }

    // Move to next step or submit
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final submission
      try {
        const response = await fetch('/api/trainer/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        const data = await response.json();

        if (!response.ok) {
          const errorMsg = data.details 
            ? `${data.error} - ${data.details}` 
            : data.error || 'Failed to register. Please try again.';
          console.error('API Error:', data);
          setToast({ message: errorMsg, type: 'error' });
          return;
        }

        console.log('Trainer registered successfully:', data);
        setShowSuccessModal(true);
      } catch (error) {
        console.error('Registration error:', error);
        setToast({ 
          message: 'Failed to register. Please check your connection and try again.', 
          type: 'error' 
        });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const specializationOptions = [
    'Yoga', 'Pilates', 'Strength Training', 'Cardio', 'HIIT',
    'CrossFit', 'Dance Fitness', 'Prenatal/Postnatal', 'Nutrition',
    'Weight Loss', 'Bodybuilding', 'Flexibility', 'Meditation'
  ];

  const serviceOptions = [
    'One-on-One Training', 'Group Classes', 'Online Coaching',
    'Nutrition Planning', 'Fitness Assessment', 'Custom Workout Plans'
  ];

  const timeSlots = ['Morning', 'Afternoon', 'Evening'];

  const environmentOptions = [
    'Women-Only Gyms', 'Home Visits', 'Online Sessions', 'Outdoor Training'
  ];

  return (
    <div className="min-h-screen bg-[#F6EFED]">
      <InnerPageHeader />

      <section className="py-20">
        <div className="max-w-[1200px] mx-auto px-[20px]">
          {/* Eyebrow & Title */}
          <p className="font-['Archivo',sans-serif] text-[14px] text-[#8a7296] font-medium mb-4 tracking-wider">
            • TRAINER REGISTRATION
          </p>

          <h1 className="font-['Gotham_Rounded',sans-serif] text-[48px] font-bold text-[#2E2A3A] mb-3">
            {currentStep === 1 && 'Personal Details'}
            {currentStep === 2 && 'Professional Information'}
            {currentStep === 3 && 'Specializations & Services'}
            {currentStep === 4 && 'Availability'}
            {currentStep === 5 && 'Preferences'}
          </h1>

          <p className="font-['Archivo',sans-serif] text-[18px] text-[#6e6672] mb-12">
            Step {currentStep} of 5 - Join our community of female fitness trainers
          </p>

          {/* Form */}
          <form onSubmit={handleNext} className="bg-white rounded-[24px] p-8 md:p-12 shadow-sm">
            
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="e.g. Sarah"
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="e.g. Johnson"
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>
                </div>

                {/* Email & Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      Email address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="e.g. sarah@example.com"
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g. 000 000 0000"
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>
                </div>

                {/* Location & DOB */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      Location <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g. Birmingham, UK"
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Professional Info */}
            {currentStep === 2 && (
              <div className="space-y-6">
                {/* Bio */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Professional Bio <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about your fitness journey, qualifications, and what makes you a great trainer..."
                    rows={6}
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors resize-none"
                  />
                  <p className="mt-1 font-['Archivo',sans-serif] text-[12px] text-[#6e6672]">
                    Minimum 100 characters ({formData.bio.length}/100)
                  </p>
                </div>

                {/* Profile Photo */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                    Profile Photo <span className="text-red-500">*</span>
                  </label>
                  
                  {formData.profilePhoto ? (
                    <div className="relative w-48 h-48 mx-auto">
                      <img
                        src={formData.profilePhoto}
                        alt="Profile"
                        className="w-full h-full object-cover rounded-full border-4 border-[#8a7296]"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, profilePhoto: '' }))}
                        className="absolute top-0 right-0 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="block w-48 h-48 mx-auto border-2 border-dashed border-[#d7d7d7] rounded-full flex flex-col items-center justify-center cursor-pointer hover:border-[#8a7296] transition-colors bg-[#f9f9f9]">
                      <input
                        type="file"
                        accept="image/png,image/jpg,image/jpeg,image/webp"
                        onChange={handleProfilePhotoUpload}
                        className="hidden"
                      />
                      <svg className="w-12 h-12 text-[#8a7296] mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">Upload Photo</span>
                    </label>
                  )}
                </div>

                {/* Years of Experience */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Years of Experience <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleInputChange}
                    placeholder="e.g. 5"
                    min="0"
                    required
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Certifications */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                    Certifications <span className="text-red-500">*</span>
                  </label>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.certifications.map((cert, index) => (
                      <div key={index} className="relative aspect-square">
                        <img
                          src={cert}
                          alt={`Certification ${index + 1}`}
                          className="w-full h-full object-cover rounded-[12px]"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveCertification(index)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    <label className="aspect-square border-2 border-dashed border-[#d7d7d7] rounded-[12px] flex flex-col items-center justify-center cursor-pointer hover:border-[#8a7296] transition-colors bg-[#f9f9f9]">
                      <input
                        type="file"
                        accept="image/png,image/jpg,image/jpeg,image/webp"
                        onChange={handleCertificationUpload}
                        multiple
                        className="hidden"
                      />
                      <svg className="w-8 h-8 text-[#8a7296] mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="font-['Archivo',sans-serif] text-[12px] text-[#6e6672] text-center px-2">Add Certificate</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Specializations & Services */}
            {currentStep === 3 && (
              <div className="space-y-6">
                {/* Specializations */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                    Specializations <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {specializationOptions.map((spec) => (
                      <button
                        key={spec}
                        type="button"
                        onClick={() => handleSpecializationToggle(spec)}
                        className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                          formData.specializations.includes(spec)
                            ? 'bg-[#8a7296] text-white'
                            : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                        }`}
                      >
                        {spec}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Services Offered */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                    Services You Offer <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {serviceOptions.map((service) => (
                      <button
                        key={service}
                        type="button"
                        onClick={() => handleServiceToggle(service)}
                        className={`px-4 py-2 rounded-full font-['Archivo',sans-serif] text-[14px] font-medium transition-all ${
                          formData.servicesOffered.includes(service)
                            ? 'bg-[#8a7296] text-white'
                            : 'bg-[#f5f2f0] text-[#2E2A3A] hover:bg-[#e5e2e0]'
                        }`}
                      >
                        {service}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Session Pricing */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Session Pricing <span className="text-[#6e6672] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="sessionPricing"
                    value={formData.sessionPricing}
                    onChange={handleInputChange}
                    placeholder="e.g. £30/hour or £150/month"
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Availability */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <p className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672] mb-4">
                  Select your available days and preferred time slots
                </p>

                {Object.entries(formData.availability).map(([day, data]) => (
                  <div
                    key={day}
                    className={`p-4 rounded-[12px] border-2 transition-colors ${
                      data.isAvailable ? 'border-[#8a7296] bg-[#8a72961a]' : 'border-[#d7d7d7] bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-['Archivo',sans-serif] text-[16px] font-medium text-[#2E2A3A]">
                        {day}
                      </span>
                      
                      <button
                        type="button"
                        onClick={() => handleDayToggle(day)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${
                          data.isAvailable ? 'bg-[#8a7296]' : 'bg-[#d7d7d7]'
                        }`}
                      >
                        <span
                          className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            data.isAvailable ? 'translate-x-6' : ''
                          }`}
                        />
                      </button>
                    </div>

                    {data.isAvailable && (
                      <div className="flex flex-wrap gap-2">
                        {timeSlots.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => handleTimeSlotToggle(day, slot)}
                            className={`px-3 py-1 rounded-full font-['Archivo',sans-serif] text-[13px] transition-all ${
                              data.timeSlots.includes(slot)
                                ? 'bg-[#8a7296] text-white'
                                : 'bg-white text-[#2E2A3A] border border-[#d7d7d7] hover:border-[#8a7296]'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Step 5: Preferences */}
            {currentStep === 5 && (
              <div className="space-y-6">
                {/* Preferred Environment */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-4">
                    Preferred Training Environment
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {environmentOptions.map((env) => (
                      <button
                        key={env}
                        type="button"
                        onClick={() => handleEnvironmentToggle(env)}
                        className={`p-4 rounded-[12px] border-2 transition-all text-left ${
                          formData.preferredEnvironment.includes(env)
                            ? 'border-[#8a7296] bg-[#8a72961a]'
                            : 'border-[#d7d7d7] bg-white hover:border-[#8a7296]'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-['Archivo',sans-serif] text-[15px] font-medium text-[#2E2A3A]">
                            {env}
                          </span>
                          {formData.preferredEnvironment.includes(env) && (
                            <svg className="w-5 h-5 text-[#8a7296]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Travel Distance */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Willing to Travel <span className="text-[#6e6672] font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    name="travelDistance"
                    value={formData.travelDistance}
                    onChange={handleInputChange}
                    placeholder="e.g. Within 10 miles"
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors"
                  />
                </div>

                {/* Additional Notes */}
                <div>
                  <label className="block font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A] mb-2">
                    Additional Notes <span className="text-[#6e6672] font-normal">(optional)</span>
                  </label>
                  <textarea
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    placeholder="Any other information you'd like to share..."
                    rows={4}
                    className="w-full px-4 py-3 border border-[#d7d7d7] rounded-[12px] bg-white font-['Archivo',sans-serif] text-[16px] focus:outline-none focus:border-[#8a7296] transition-colors resize-none"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-between items-center pt-6">
              <button
                type="button"
                onClick={currentStep === 1 ? () => router.push('/') : handleBack}
                className="px-6 py-3 font-['Archivo',sans-serif] text-[16px] text-[#6e6672] hover:text-[#2E2A3A] transition-colors"
              >
                {currentStep === 1 ? 'Cancel' : '← Back'}
              </button>
              <button
                type="submit"
                className="px-8 py-3 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center gap-2"
              >
                {currentStep === 5 ? 'Finish' : 'Next'}
                {currentStep < 5 && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                )}
              </button>
            </div>

            {/* Progress Bar */}
            <div className="mt-8 pt-6 border-t border-[#e5e5e5]">
              <div className="flex justify-between items-center mb-2">
                <span className="font-['Archivo',sans-serif] text-[14px] text-[#6e6672]">
                  Progress
                </span>
                <span className="font-['Archivo',sans-serif] text-[14px] font-medium text-[#2E2A3A]">
                  {currentStep} of 5
                </span>
              </div>
              <div className="w-full bg-[#e5e5e5] rounded-full h-2">
                <div
                  className="bg-[#8a7296] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / 5) * 100}%` }}
                />
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[24px] max-w-[500px] w-full p-8 relative animate-in fade-in zoom-in duration-300">
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-[#6e6672] hover:text-[#2E2A3A] text-2xl transition-colors"
            >
              ×
            </button>

            <div className="w-24 h-24 mx-auto mb-6">
              <img 
                src="/listed-done.jpg" 
                alt="Success" 
                className="w-full h-full object-contain"
              />
            </div>

            <h2 className="font-['Gotham_Rounded',sans-serif] text-[28px] font-bold text-[#2E2A3A] text-center mb-4">
              You're all set!
            </h2>

            <p className="font-['Archivo',sans-serif] text-[16px] text-[#6e6672] text-center leading-relaxed mb-8">
              Your trainer profile is now live! Women looking for fitness guidance can discover and book sessions with you. Start connecting with clients today.
            </p>

            <button
              onClick={() => router.push('/dashboard')}
              className="w-full px-8 py-4 bg-[#8a7296] text-white font-['Archivo',sans-serif] text-[16px] font-medium rounded-[10px] hover:bg-[#7a647d] transition-colors flex items-center justify-center gap-2"
            >
              Go To Dashboard
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
